import { MermaidConfig } from 'mermaid';
import { StageDetails } from '../types/processFlowTypes';

// Mermaid configuration
export const mermaidConfig: Partial<MermaidConfig> = {
  startOnLoad: true,
  theme: 'default',
  flowchart: {
    curve: 'basis',
    padding: 20,
  },
  themeVariables: {
    primaryColor: '#2196f3',
    primaryTextColor: '#ffffff',
    primaryBorderColor: '#1976d2',
    lineColor: '#2196f3',
    secondaryColor: '#ff9800',
    tertiaryColor: '#4caf50'
  }
};

// Define the process flow diagram
export const processFlowDiagram = `
graph TD
    subgraph "🚀 Getting Started"
        A[📤 Upload Your Keywords] --> B[⚙️ Tell Us About Your Business]
    end

    subgraph "🧠 Smart Analysis"
        B --> C[🤖 AI Analyzes Keywords]
        C --> D[📊 Creates Smart Categories]
        D --> E[✨ Classifies Each Keyword]
    end

    subgraph "🎲 Smart Sampling"
        E --> F[🎯 Sample Keywords for Enrichment]
    end

    subgraph "💎 Data Enrichment"
        F --> G[🔍 Adds Search Volume Data]
        F --> H[📈 Adds SERP Data]
        G --> I[🎯 Enrichment Complete]
        H --> I
    end

    subgraph "🎯 Your Results"
        I --> J[📊 Explore Interactive Dashboard]
        I --> K[💬 Chat with AI Assistant]
        J --> L[💡 Get Valuable Insights]
        K --> L
    end

    classDef setup fill:#e8f5e9,stroke:#2e7d32,color:#1b5e20,stroke-width:2px
    classDef analysis fill:#fff8e1,stroke:#f9a825,color:#f57f17,stroke-width:2px
    classDef sampling fill:#e1f5fe,stroke:#0277bd,color:#01579b,stroke-width:2px
    classDef enrich fill:#f3e5f5,stroke:#7b1fa2,color:#4a148c,stroke-width:2px
    classDef insights fill:#e3f2fd,stroke:#1565c0,color:#0d47a1,stroke-width:2px

    class A,B setup
    class C,D,E analysis
    class F sampling
    class G,H,I enrich
    class J,K,L insights
`;

// Define mapping between Mermaid node IDs and stage categories
export const nodeToStageMapping: Record<string, string> = {
  'A': 'start', 'B': 'start',
  'C': 'analysis', 'D': 'analysis', 'E': 'analysis',
  'F': 'sampling',
  'G': 'enrich', 'H': 'enrich', 'I': 'enrich',
  'J': 'results', 'K': 'results', 'L': 'results'
};

// Add more detailed information for each stage
export const stageDetails: Record<string, StageDetails> = {
  'start': {
    title: '🚀 Getting Started',
    description: 'Quick and easy setup - just upload your keywords and tell us about your goals.',
    steps: [
      { 
        id: 'A', 
        label: '📤 Upload Your Keywords',
        description: 'Import keywords from your favorite tools with all their existing metrics intact.',
        instructions: '📊 Supported data sources:\n• Google Ads (with CPC, competition, search volume)\n• Google Search Console (impressions, clicks, CTR, position)\n• Local Business data (local search volumes, map rankings)\n• SEMrush exports (difficulty, trends, SERP features)\n\n✅ All metrics carry through to your enriched results!'
      },
      { 
        id: 'B', 
        label: '⚙️ Tell Us About Your Business',
        description: 'A few quick questions help us understand your specific needs and goals.',
        instructions: '• What industry are you in?\n• Who\'s your target audience?\n• What are your main objectives?\n\nTakes less than 2 minutes!'
      }
    ]
  },
  'analysis': {
    title: '🧠 Smart Analysis',
    description: 'Watch as our AI works its magic, understanding and organizing your keywords intelligently.',
    steps: [
      { 
        id: 'C', 
        label: '🤖 AI Analyzes Keywords',
        description: 'Our AI reads and understands each keyword\'s meaning, intent, and business value.',
        instructions: '⏱️ Processing time: ~30 seconds per 1,000 keywords\n\nYou\'ll see:\n• Real-time progress updates\n• Keywords being analyzed\n• Initial patterns emerging'
      },
      { 
        id: 'D', 
        label: '📊 Creates Smart Categories',
        description: 'Keywords are automatically grouped into logical, business-relevant categories.',
        instructions: '✅ Categories are created based on:\n• Your industry context\n• Semantic relationships\n• User intent patterns\n\nYou can always adjust these later!'
      },
      { 
        id: 'E', 
        label: '✨ Classifies Each Keyword',
        description: 'Every keyword gets assigned to the perfect category with a confidence score.',
        instructions: '🎯 High-confidence matches: Automatically classified\n⚠️ Uncertain matches: Flagged for your review\n✏️ Full control: Adjust any classification manually\n\n💾 All classifications are automatically saved to your workspace.'
      }
    ]
  },
  'sampling': {
    title: '🎲 Smart Sampling',
    description: 'Intelligently select keywords for cost-effective enrichment while maintaining comprehensive insights.',
    steps: [
      { 
        id: 'F', 
        label: '🎯 Sample Keywords for Enrichment',
        description: 'Choose a smart sample of keywords to enrich with external data, saving time and API costs.',
        instructions: '🎲 Sampling strategies:\n• Representative sampling across all categories\n• High-volume priority sampling\n• Stratified sampling by impressions\n• Custom sampling based on your criteria\n\n💡 Sample 100-1000 keywords to understand patterns across your entire dataset\n💰 Save up to 90% on API costs while maintaining accuracy'
      }
    ]
  },
  'enrich': {
    title: '💎 Data Enrichment',
    description: 'Transform sampled keywords into actionable data with search metrics and SERP insights.',
    steps: [
      { 
        id: 'G', 
        label: '🔍 Adds Search Volume Data',
        description: 'Get accurate monthly search volumes and trends for your sampled keywords.',
        instructions: 'Discover:\n• Monthly search volumes\n• Seasonal trends\n• Geographic variations\n• Cost-per-click data\n\n📊 Results automatically extrapolated to full dataset'
      },
      { 
        id: 'H', 
        label: '📈 Adds SERP Data',
        description: 'Understand keyword presence across different SERP features for your sample.',
        instructions: 'SERP feature analysis:\n• Featured snippets opportunities\n• People Also Ask presence\n• Local pack appearances\n• Knowledge panel triggers\n• Shopping results potential\n• Video carousel opportunities'
      },
      {
        id: 'I',
        label: '🎯 Enrichment Complete',
        description: 'All enrichment data is processed and ready for analysis.',
        instructions: '✅ Sample data collected\n📊 Patterns identified\n🔄 Insights applied to full dataset\n💾 Everything automatically saved and backed up'
      }
    ]
  },
  'results': {
    title: '🎯 Your Results',
    description: 'Explore your data through powerful visualizations and get instant answers to your questions.',
    steps: [
      { 
        id: 'J', 
        label: '📊 Explore Interactive Dashboard',
        description: 'See how your smart categories perform with all your imported and enriched metrics.',
        instructions: '📈 Category performance metrics:\n• Total impressions by category\n• Average CTR per category\n• Search volume distribution\n• SERP feature opportunities\n• Revenue potential by category\n\n🔍 Filter and analyze by any metric from your original data!'
      },
      { 
        id: 'K', 
        label: '💬 Chat with AI Assistant',
        description: 'Ask questions in plain English and get instant answers about your data.',
        instructions: 'Try asking:\n• "What are my top opportunity keywords?"\n• "Show me high-volume, low-competition terms"\n• "Which categories are trending up?"\n• "What should I focus on first?"'
      },
      { 
        id: 'L', 
        label: '💡 Get Valuable Insights',
        description: 'Discover actionable insights that drive real business results.',
        instructions: '🎯 Strategic recommendations\n📈 Growth opportunities\n🔍 Hidden keyword gems\n💰 ROI projections\n\nExport your insights anytime to CSV, PDF, or integrate with your tools!'
      }
    ]
  }
};
